import { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, Plus, Download, Trash2, ExternalLink, Loader2, AlertCircle, CheckCircle, Sparkles, ShieldCheck, Link, ThumbsUp, Star, Pencil, Search, Clock, RefreshCw, Library, Upload, FileUp, X } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from './ui';
import InlineLicenseForm, {
  LicenseFormData,
  DetectedLicense,
  getDefaultLicenseFormData,
  LicenseType
} from './InlineLicenseForm';
import { SharedLiteratureBrowser } from './SharedLiteratureBrowser';

// Debounce utility
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

interface Literature {
  id: string;
  title: string;
  description: string;
  literature_type: string;
  original_url: string;
  local_file_path: string | null;
  local_file_size: number | null;
  download_status: string;
  download_error: string | null;
  ai_summary: string | null;
  ai_relevance_score: number | null;
  added_by_ai: boolean;
  display_order: number;
  // New fields for linked literature
  source_type?: 'manual' | 'linked';
  shared_literature_id?: string;
  author?: string;
  publisher?: string;
  publication_year?: number;
  isbn?: string;
  doi?: string;
  // License info (will be fetched separately or included)
  license_type?: string;
  license_confirmed?: boolean;
}

interface LiteratureManagerProps {
  studyPlanId: string;
  readOnly?: boolean;
  onLiteratureChange?: () => void;
}

export default function LiteratureManager({ studyPlanId, readOnly = false, onLiteratureChange }: LiteratureManagerProps) {
  const { confirm } = useConfirm();
  const [literature, setLiterature] = useState<Literature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add manual modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    description: '',
    literature_type: 'pdf',
    original_url: '',
    author: '',
    publisher: '',
    publication_year: '',
    isbn: '',
    doi: ''
  });

  // Library browser modal
  const [showLibraryBrowser, setShowLibraryBrowser] = useState(false);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [archiveOnAdd, setArchiveOnAdd] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // License form state
  const [licenseForm, setLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [licenseConfirmed, setLicenseConfirmed] = useState(false);
  const [detectedLicense, setDetectedLicense] = useState<DetectedLicense | null>(null);
  const [isDetectingLicense, setIsDetectingLicense] = useState(false);

  // AI suggestions
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [disclaimer, setDisclaimer] = useState<string>('');

  // Edit AI suggestion before adding
  const [editingSuggestion, setEditingSuggestion] = useState<any | null>(null);
  const [suggestionEditForm, setSuggestionEditForm] = useState({
    title: '',
    description: '',
    url: '',
    literature_type: 'webpage',
    author: '',
    publisher: ''
  });
  const [suggestionLicenseForm, setSuggestionLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [suggestionLicenseConfirmed, setSuggestionLicenseConfirmed] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLit, setEditingLit] = useState<Literature | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    literature_type: 'pdf',
    original_url: '',
    author: '',
    publisher: '',
    publication_year: '',
    isbn: '',
    doi: '',
    archive_locally: false
  });
  const [editLicenseForm, setEditLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [editLicenseConfirmed, setEditLicenseConfirmed] = useState(false);
  const [showLicenseInEdit, setShowLicenseInEdit] = useState(false);

  // URL Correction modal
  const [showUrlCorrectionModal, setShowUrlCorrectionModal] = useState(false);
  const [urlCorrectionLit, setUrlCorrectionLit] = useState<Literature | null>(null);
  const [urlSuggestions, setUrlSuggestions] = useState<any[]>([]);
  const [loadingUrlSuggestions, setLoadingUrlSuggestions] = useState(false);
  const [applyingUrl, setApplyingUrl] = useState<string | null>(null);

  useEffect(() => {
    loadLiterature();
  }, [studyPlanId]);

  // Debounced license detection from URL
  const detectLicenseFromUrl = useCallback(
    debounce(async (url: string) => {
      if (!url || !url.startsWith('http')) {
        setDetectedLicense(null);
        return;
      }

      setIsDetectingLicense(true);
      try {
        const response = await authenticatedFetch('/api/literature/detect-license', {
          method: 'POST',
          body: JSON.stringify({ url })
        });
        const data = await response.json();

        if (data.success && data.detected) {
          setDetectedLicense(data);
        } else {
          setDetectedLicense(null);
        }
      } catch (err) {
        console.error('License detection failed:', err);
        setDetectedLicense(null);
      } finally {
        setIsDetectingLicense(false);
      }
    }, 500),
    []
  );

  // Trigger license detection when URL changes
  useEffect(() => {
    if (showAddModal && addForm.original_url) {
      detectLicenseFromUrl(addForm.original_url);
    }
  }, [addForm.original_url, showAddModal]);

  // Reset license form when modal closes
  useEffect(() => {
    if (!showAddModal) {
      setLicenseForm(getDefaultLicenseFormData());
      setLicenseConfirmed(false);
      setDetectedLicense(null);
      setUploadedFile(null);
      setUploadProgress(null);
    }
  }, [showAddModal]);

  // File upload handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/html', 'text/plain'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.html', '.htm', '.txt'];

    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      setError('Ugyldig filtype. Støttede formater: PDF, Word, HTML, TXT');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('Filen er for stor. Maks størrelse er 50 MB.');
      return;
    }

    setUploadedFile(file);
    setAddForm(prev => ({
      ...prev,
      title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
      literature_type: file.type.includes('pdf') ? 'pdf' :
                       file.type.includes('word') ? 'word' :
                       file.type.includes('html') ? 'webpage' : 'other',
      original_url: '' // Clear URL when file is selected
    }));
    setError('');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle library selection
  const handleLibrarySelect = (lit: any) => {
    setShowLibraryBrowser(false);
    loadLiterature(); // Refresh list
    setSuccess(`"${lit.title}" ble lagt til i studieplanen`);
    if (onLiteratureChange) {
      onLiteratureChange();
    }
  };

  const loadLiterature = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`);
      const data = await response.json();

      if (data.success) {
        setLiterature(data.literature);
      } else {
        setError(data.message || 'Kunne ikke laste litteratur');
      }
    } catch (err) {
      console.error('Error loading literature:', err);
      setError('Kunne ikke laste litteratur');
    } finally {
      setLoading(false);
    }
  };

  const handleAddManual = async () => {
    // Validate: either file or URL must be provided
    if (!addForm.title) {
      setError('Tittel er påkrevd');
      return;
    }

    if (!uploadedFile && !addForm.original_url) {
      setError('Du må enten laste opp en fil eller oppgi en URL');
      return;
    }

    if (!licenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen før du kan legge til litteratur');
      return;
    }

    if (licenseForm.license_type === 'unknown') {
      setError('Du må velge en lisenstype');
      return;
    }

    if (licenseForm.license_type === 'creative_commons' && !licenseForm.cc_variant) {
      setError('Du må velge en CC-variant for Creative Commons-lisenser');
      return;
    }

    try {
      let response;

      if (uploadedFile) {
        // File upload mode
        setUploadProgress(0);
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('title', addForm.title);
        formData.append('description', addForm.description);
        formData.append('literature_type', addForm.literature_type);
        formData.append('license_type', licenseForm.license_type);
        if (licenseForm.cc_variant) formData.append('cc_variant', licenseForm.cc_variant);
        formData.append('allows_distribution', String(licenseForm.allows_distribution));
        formData.append('allows_modification', String(licenseForm.allows_modification));
        formData.append('allows_commercial_use', String(licenseForm.allows_commercial_use));
        formData.append('requires_attribution', String(licenseForm.requires_attribution));
        if (licenseForm.attribution_text) formData.append('attribution_text', licenseForm.attribution_text);
        if (licenseForm.rights_holder) formData.append('rights_holder', licenseForm.rights_holder);
        if (licenseForm.license_url) formData.append('license_url', licenseForm.license_url);
        if (licenseForm.valid_until) formData.append('valid_until', licenseForm.valid_until);
        formData.append('is_perpetual', String(licenseForm.is_perpetual));
        formData.append('is_organization_specific', String(licenseForm.is_organization_specific));
        if (licenseForm.max_users) formData.append('max_users', String(licenseForm.max_users));
        if (licenseForm.usage_notes) formData.append('usage_notes', licenseForm.usage_notes);
        formData.append('license_confirmed', 'true');

        // Use XMLHttpRequest for progress tracking
        response = await new Promise<Response>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `/api/study-plans/${studyPlanId}/literature/upload`);

          // Get auth token
          const authData = localStorage.getItem('auth');
          if (authData) {
            const { auth_token } = JSON.parse(authData);
            if (auth_token) {
              xhr.setRequestHeader('Authorization', `Bearer ${auth_token}`);
            }
          }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
          };

          xhr.onload = () => {
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText
            });
            resolve(response);
          };

          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.send(formData);
        });
      } else {
        // URL mode
        response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: addForm.title,
            description: addForm.description,
            literature_type: addForm.literature_type,
            original_url: addForm.original_url,
            author: addForm.author || null,
            publisher: addForm.publisher || null,
            publication_year: addForm.publication_year ? parseInt(addForm.publication_year) : null,
            isbn: addForm.isbn || null,
            doi: addForm.doi || null,
            archive_on_add: archiveOnAdd,
            // License fields
            license_type: licenseForm.license_type,
            cc_variant: licenseForm.cc_variant,
            allows_distribution: licenseForm.allows_distribution,
            allows_modification: licenseForm.allows_modification,
            allows_commercial_use: licenseForm.allows_commercial_use,
            requires_attribution: licenseForm.requires_attribution,
            attribution_text: licenseForm.attribution_text,
            rights_holder: licenseForm.rights_holder,
            license_url: licenseForm.license_url,
            valid_until: licenseForm.valid_until,
            is_perpetual: licenseForm.is_perpetual,
            is_organization_specific: licenseForm.is_organization_specific,
            max_users: licenseForm.max_users,
            usage_notes: licenseForm.usage_notes,
            license_confirmed: true
          })
        });
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(uploadedFile ? 'Fil lastet opp og lagt til' : 'Litteratur lagt til med lisensbekreftelse');
        setShowAddModal(false);
        setAddForm({ title: '', description: '', literature_type: 'pdf', original_url: '', author: '', publisher: '', publication_year: '', isbn: '', doi: '' });
        setLicenseForm(getDefaultLicenseFormData());
        setLicenseConfirmed(false);
        setUploadedFile(null);
        setUploadProgress(null);
        loadLiterature();
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke legge til litteratur');
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
      setUploadProgress(null);
    }
  };

  const handleAISuggest = async () => {
    try {
      setLoadingSuggestions(true);
      setError('');

      const response = await authenticatedFetch('/api/ai/suggest-literature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ study_plan_id: studyPlanId, count: 5 })
      });

      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions);
        setDisclaimer(data.disclaimer || '');
        setShowSuggestionsModal(true);
      } else {
        setError('Kunne ikke generere forslag');
      }
    } catch (err) {
      setError('Kunne ikke generere forslag');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: any) => {
    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestion.title,
          description: suggestion.summary,
          literature_type: suggestion.literature_type,
          original_url: suggestion.url,
          added_by_ai: true,
          ai_relevance_score: suggestion.relevance_score
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Litteratur lagt til fra KI-forslag');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== suggestion));
        onLiteratureChange?.();
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
    }
  };

  const handleApproveAndAddToCurated = async (suggestion: any) => {
    try {
      // Use the new request flow - adds to study plan AND sends request to admin
      const response = await authenticatedFetch('/api/curated-sources/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestion.title,
          url: suggestion.url,
          literature_type: suggestion.literature_type,
          description: suggestion.summary,
          study_plan_id: studyPlanId,
          ai_relevance_score: suggestion.relevance_score
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', response.status, errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.error || errorData.message || `Feil ${response.status}`);
        } catch {
          setError(`Serverfeil: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message || 'Kilde lagt til i studieplanen. Forespørsel sendt til studieforbundet.');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== suggestion));
        onLiteratureChange?.();
      } else {
        setError(data.error || 'Kunne ikke legge til kilde');
      }
    } catch (err: any) {
      console.error('handleApproveAndAddToCurated error:', err);
      setError(err.message || 'Kunne ikke legge til kilde');
    }
  };

  // Open suggestion for editing before adding
  const handleEditSuggestion = (suggestion: any) => {
    setEditingSuggestion(suggestion);
    setSuggestionEditForm({
      title: suggestion.title || '',
      description: suggestion.summary || '',
      url: suggestion.url || '',
      literature_type: suggestion.literature_type || 'webpage',
      author: suggestion.author || '',
      publisher: suggestion.publisher || ''
    });
    setSuggestionLicenseForm(getDefaultLicenseFormData());
    setSuggestionLicenseConfirmed(false);
  };

  // Add edited suggestion
  const handleAddEditedSuggestion = async () => {
    if (!suggestionEditForm.title || !suggestionEditForm.url) {
      setError('Tittel og URL er påkrevd');
      return;
    }

    if (!suggestionLicenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen');
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestionEditForm.title,
          description: suggestionEditForm.description,
          literature_type: suggestionEditForm.literature_type,
          original_url: suggestionEditForm.url,
          author: suggestionEditForm.author,
          publisher: suggestionEditForm.publisher,
          added_by_ai: true,
          ai_relevance_score: editingSuggestion?.relevance_score,
          // License fields
          license_type: suggestionLicenseForm.license_type,
          cc_variant: suggestionLicenseForm.cc_variant,
          allows_distribution: suggestionLicenseForm.allows_distribution,
          allows_modification: suggestionLicenseForm.allows_modification,
          allows_commercial_use: suggestionLicenseForm.allows_commercial_use,
          requires_attribution: suggestionLicenseForm.requires_attribution,
          attribution_text: suggestionLicenseForm.attribution_text,
          rights_holder: suggestionLicenseForm.rights_holder,
          license_confirmed: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('KI-forslag redigert og lagt til');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== editingSuggestion));
        setEditingSuggestion(null);
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke legge til litteratur');
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
    }
  };

  const handleDownload = async (litId: string) => {
    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${litId}/download`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Fil lastet ned og lagret');
        loadLiterature();
      } else {
        setError(data.message || 'Nedlasting feilet');
      }
    } catch (err) {
      setError('Nedlasting feilet');
    }
  };

  const handleDelete = async (litId: string) => {
    const confirmed = await confirm({
      title: 'Fjern litteratur',
      message: 'Er du sikker på at du vil fjerne denne litteraturen?',
      confirmLabel: 'Fjern',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${litId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Litteratur fjernet');
        loadLiterature();
        onLiteratureChange?.();
      }
    } catch (err) {
      setError('Kunne ikke fjerne litteratur');
    }
  };

  const handleEdit = (lit: Literature) => {
    setEditingLit(lit);
    setEditForm({
      title: lit.title,
      description: lit.description || '',
      literature_type: lit.literature_type,
      original_url: lit.original_url,
      author: lit.author || '',
      publisher: lit.publisher || '',
      publication_year: lit.publication_year?.toString() || '',
      isbn: lit.isbn || '',
      doi: lit.doi || '',
      archive_locally: lit.download_status === 'downloaded'
    });
    // Set existing license info if available
    if (lit.license_type) {
      setEditLicenseForm({
        ...getDefaultLicenseFormData(),
        license_type: lit.license_type as LicenseType,
      });
      setEditLicenseConfirmed(lit.license_confirmed || false);
      setShowLicenseInEdit(false);
    } else {
      setEditLicenseForm(getDefaultLicenseFormData());
      setEditLicenseConfirmed(false);
      setShowLicenseInEdit(false);
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingLit || !editForm.title || !editForm.original_url) {
      setError('Tittel og URL er påkrevd');
      return;
    }

    // If editing license, require confirmation
    if (showLicenseInEdit && !editLicenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen');
      return;
    }

    try {
      const payload: any = { ...editForm };

      // Include license info if editing license
      if (showLicenseInEdit) {
        payload.license_type = editLicenseForm.license_type;
        payload.cc_variant = editLicenseForm.cc_variant;
        payload.allows_distribution = editLicenseForm.allows_distribution;
        payload.allows_modification = editLicenseForm.allows_modification;
        payload.allows_commercial_use = editLicenseForm.allows_commercial_use;
        payload.requires_attribution = editLicenseForm.requires_attribution;
        payload.attribution_text = editLicenseForm.attribution_text;
        payload.rights_holder = editLicenseForm.rights_holder;
        payload.license_url = editLicenseForm.license_url;
        payload.valid_until = editLicenseForm.valid_until;
        payload.is_perpetual = editLicenseForm.is_perpetual;
        payload.is_organization_specific = editLicenseForm.is_organization_specific;
        payload.max_users = editLicenseForm.max_users;
        payload.usage_notes = editLicenseForm.usage_notes;
        payload.license_confirmed = true;
      }

      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${editingLit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Litteratur oppdatert');
        setShowEditModal(false);
        setEditingLit(null);
        setShowLicenseInEdit(false);
        loadLiterature();
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke oppdatere litteratur');
      }
    } catch (err) {
      setError('Kunne ikke oppdatere litteratur');
    }
  };

  // Find alternative URL for broken links
  const handleFindAlternativeUrl = async (lit: Literature) => {
    setUrlCorrectionLit(lit);
    setUrlSuggestions([]);
    setShowUrlCorrectionModal(true);
    setLoadingUrlSuggestions(true);
    setError('');

    try {
      const response = await authenticatedFetch(`/api/literature/${lit.id}/find-alternative`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        setUrlSuggestions(data.suggestions || []);
        if (data.suggestions?.length === 0) {
          setError('Ingen alternative URLer funnet');
        }
      } else {
        setError(data.message || 'Kunne ikke søke etter alternativ URL');
      }
    } catch (err) {
      setError('Kunne ikke søke etter alternativ URL');
    } finally {
      setLoadingUrlSuggestions(false);
    }
  };

  // Apply corrected URL
  const handleApplyUrlCorrection = async (newUrl: string, method: string) => {
    if (!urlCorrectionLit) return;

    setApplyingUrl(newUrl);
    setError('');

    try {
      const response = await authenticatedFetch(`/api/literature/${urlCorrectionLit.id}/correct-url`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_url: newUrl,
          method: method,
          table: 'study_plan'
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('URL oppdatert! Kilden kan nå lastes ned på nytt.');
        setShowUrlCorrectionModal(false);
        setUrlCorrectionLit(null);
        setUrlSuggestions([]);
        loadLiterature();
        // Note: We don't call onLiteratureChange here because URL corrections
        // should not trigger revalidation (as per user requirement)
      } else {
        setError(data.message || 'Kunne ikke oppdatere URL');
      }
    } catch (err) {
      setError('Kunne ikke oppdatere URL');
    } finally {
      setApplyingUrl(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-kf-primary" />
          Litteraturliste
        </h3>

        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleAISuggest}
              disabled={loadingSuggestions}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {loadingSuggestions ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Genererer...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  KI-forslag
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setShowLibraryBrowser(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm"
            >
              <Library className="h-4 w-4" />
              Velg fra bibliotek
            </button>

            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary flex items-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" />
              Legg til manuelt
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border-l-4 border-green-400 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {literature.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Ingen litteratur lagt til ennå</p>
          <p className="text-sm text-gray-500 mt-1">
            Støttede formater: PDF, Word, HTML (kun dokumenter, maks 50 MB)
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {literature.map((lit) => (
            <div key={lit.id} className="p-4 bg-white border rounded-lg hover:shadow-md transition">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900 min-w-0">{lit.title}</h4>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {/* Source type badge */}
                      {lit.source_type === 'linked' && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Library className="h-3 w-3" />
                          Fra bibliotek
                        </span>
                      )}
                      {lit.added_by_ai && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Sparkles className="h-3 w-3" />
                          KI-forslag
                        </span>
                      )}
                      {lit.download_status === 'downloaded' && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <CheckCircle className="h-3 w-3" />
                          Lagret lokalt
                        </span>
                      )}
                      {lit.license_type && lit.license_type !== 'unknown' && (
                        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 whitespace-nowrap ${
                          lit.license_type === 'public_domain' ? 'bg-green-100 text-green-700' :
                          lit.license_type === 'creative_commons' ? 'bg-blue-100 text-blue-700' :
                          lit.license_type === 'open_access' ? 'bg-teal-100 text-teal-700' :
                          lit.license_type === 'citation_right' ? 'bg-indigo-100 text-indigo-700' :
                          lit.license_type === 'kopinor_agreement' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          <ShieldCheck className="h-3 w-3" />
                          {lit.license_type === 'public_domain' ? 'Offentlig domene' :
                           lit.license_type === 'creative_commons' ? 'CC-lisens' :
                           lit.license_type === 'open_access' ? 'Åpen tilgang' :
                           lit.license_type === 'citation_right' ? 'Sitatrett' :
                           lit.license_type === 'kopinor_agreement' ? 'Kopinor' :
                           lit.license_type === 'proprietary' ? 'Proprietær' :
                           lit.license_type === 'subscription' ? 'Abonnement' :
                           lit.license_type === 'purchased' ? 'Kjøpt' :
                           lit.license_type === 'educational' ? 'Utdanning' :
                           lit.license_type}
                        </span>
                      )}
                      {lit.license_confirmed && !lit.license_type && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <ShieldCheck className="h-3 w-3" />
                          Lisens bekreftet
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Author and publisher info */}
                  {(lit.author || lit.publisher) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {lit.author && <span>{lit.author}</span>}
                      {lit.author && lit.publisher && <span> • </span>}
                      {lit.publisher && <span>{lit.publisher}</span>}
                      {lit.publication_year && <span> ({lit.publication_year})</span>}
                    </p>
                  )}

                  {lit.description && (
                    <p className="text-sm text-gray-600 mt-1">{lit.description}</p>
                  )}

                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                    <span className="capitalize">{lit.literature_type}</span>
                    {lit.local_file_size && (
                      <span>{Math.round(lit.local_file_size / 1024)} KB</span>
                    )}
                    {lit.ai_relevance_score && (
                      <span>Relevans: {lit.ai_relevance_score}%</span>
                    )}
                    {lit.isbn && <span>ISBN: {lit.isbn}</span>}
                    {lit.doi && <span>DOI: {lit.doi}</span>}
                  </div>

                  {lit.download_status === 'failed' && lit.download_error && (
                    <div className="mt-2">
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {lit.download_error}
                      </p>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleFindAlternativeUrl(lit)}
                          className="mt-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 text-sm flex items-center gap-2"
                        >
                          <Search className="h-4 w-4" />
                          Finn alternativ URL
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <a
                    href={lit.original_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-600 hover:text-kf-primary hover:bg-gray-100 rounded-lg transition"
                    title="Åpne original URL"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleEdit(lit)}
                      className="p-2 text-gray-600 hover:text-kf-primary hover:bg-gray-100 rounded-lg transition"
                      title="Rediger litteratur"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}

                  {!readOnly && lit.download_status !== 'downloaded' && (
                    <button
                      type="button"
                      onClick={() => handleDownload(lit.id)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                      title="Last ned og lagre lokalt"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDelete(lit.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Fjern litteratur"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Manual Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4">Legg til litteratur manuelt</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={addForm.title}
                  onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  placeholder="F.eks. 'Introduksjon til pedagogikk'"
                />
              </div>

              {/* File Upload or URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fil eller URL *
                </label>

                {/* File Upload Area */}
                {!uploadedFile && !addForm.original_url && (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      isDragging
                        ? 'border-kf-primary bg-kf-primary/5'
                        : 'border-gray-300 hover:border-kf-primary hover:bg-gray-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.html,.htm,.txt"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-kf-primary' : 'text-gray-400'}`} />
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-kf-primary">Klikk for å laste opp</span>
                      {' '}eller dra og slipp fil her
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      PDF, Word, HTML (maks 50 MB)
                    </p>
                  </div>
                )}

                {/* Uploaded File Preview */}
                {uploadedFile && (
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileUp className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearUploadedFile}
                      className="p-1 hover:bg-green-100 rounded-full"
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress !== null && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Laster opp...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-kf-primary transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Divider */}
                {!uploadedFile && (
                  <div className="flex items-center gap-4 my-4">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-sm text-gray-500">eller</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                )}

                {/* URL Input */}
                {!uploadedFile && (
                  <div>
                    <input
                      type="url"
                      value={addForm.original_url}
                      onChange={(e) => setAddForm({ ...addForm, original_url: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                      placeholder="https://example.com/document.pdf"
                    />

                    {/* Archive checkbox for URL */}
                    {addForm.original_url && (
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={archiveOnAdd}
                          onChange={(e) => setArchiveOnAdd(e.target.checked)}
                          className="rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
                        />
                        <span className="text-sm text-gray-700">
                          Last ned og lagre lokalt (anbefalt for permanent tilgang)
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>

              {/* Author and Publisher */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forfatter
                  </label>
                  <input
                    type="text"
                    value={addForm.author}
                    onChange={(e) => setAddForm({ ...addForm, author: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Ola Nordmann"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgiver
                  </label>
                  <input
                    type="text"
                    value={addForm.publisher}
                    onChange={(e) => setAddForm({ ...addForm, publisher: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Universitetsforlaget"
                  />
                </div>
              </div>

              {/* Publication details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgivelsesår
                  </label>
                  <input
                    type="number"
                    value={addForm.publication_year}
                    onChange={(e) => setAddForm({ ...addForm, publication_year: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. 2024"
                    min="1900"
                    max="2100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={addForm.isbn}
                    onChange={(e) => setAddForm({ ...addForm, isbn: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="978-82-..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DOI
                  </label>
                  <input
                    type="text"
                    value={addForm.doi}
                    onChange={(e) => setAddForm({ ...addForm, doi: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="10.1000/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={addForm.literature_type}
                  onChange={(e) => setAddForm({ ...addForm, literature_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                >
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="webpage">Nettside</option>
                  <option value="book">Bok</option>
                  <option value="article">Artikkel</option>
                  <option value="video">Video</option>
                  <option value="manual">Manual/Instruksjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={addForm.description}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  rows={3}
                  placeholder="Kort beskrivelse av innholdet..."
                />
              </div>

              {/* License Form - Required before adding */}
              <InlineLicenseForm
                value={licenseForm}
                onChange={setLicenseForm}
                detectedLicense={detectedLicense}
                isDetecting={isDetectingLicense}
                confirmed={licenseConfirmed}
                onConfirmChange={setLicenseConfirmed}
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleAddManual}
                disabled={!licenseConfirmed}
                className={`px-4 py-2 rounded-lg ${
                  licenseConfirmed
                    ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Legg til
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions Modal */}
      {showSuggestionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              KI-forslag på litteratur
            </h3>

            {/* Disclaimer */}
            {disclaimer && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 font-medium">{disclaimer}</p>
                </div>
              </div>
            )}

            {suggestions.length === 0 ? (
              <p className="text-gray-600 text-center py-8">Ingen forslag funnet</p>
            ) : (
              <div className="space-y-4">
                {suggestions.map((suggestion, index) => (
                  <div key={index} className={`p-4 rounded-lg ${editingSuggestion === suggestion ? 'bg-blue-50 border-2 border-blue-300' : 'bg-purple-50 border border-purple-200'}`}>
                    {/* Show edit form inline if this suggestion is being edited */}
                    {editingSuggestion === suggestion ? (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Pencil className="h-5 w-5 text-blue-600" />
                          Rediger forslag før tillegg
                        </h4>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                            <input
                              type="text"
                              value={suggestionEditForm.title}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, title: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Forfatter</label>
                              <input
                                type="text"
                                value={suggestionEditForm.author}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, author: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                                placeholder="F.eks. NDLA"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Utgiver</label>
                              <input
                                type="text"
                                value={suggestionEditForm.publisher}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, publisher: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                                placeholder="F.eks. Utdanningsdirektoratet"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                            <input
                              type="url"
                              value={suggestionEditForm.url}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, url: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                              <select
                                value={suggestionEditForm.literature_type}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, literature_type: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                              >
                                <option value="pdf">PDF</option>
                                <option value="webpage">Nettside</option>
                                <option value="book">Bok</option>
                                <option value="article">Artikkel</option>
                                <option value="video">Video</option>
                                <option value="other">Annet</option>
                              </select>
                            </div>
                            <div className="flex items-end">
                              <p className="text-sm text-gray-500">
                                Relevans: {suggestion.relevance_score}%
                              </p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                            <textarea
                              value={suggestionEditForm.description}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, description: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                              rows={3}
                            />
                          </div>

                          {/* License form for AI suggestion */}
                          <InlineLicenseForm
                            value={suggestionLicenseForm}
                            onChange={setSuggestionLicenseForm}
                            detectedLicense={null}
                            isDetecting={false}
                            confirmed={suggestionLicenseConfirmed}
                            onConfirmChange={setSuggestionLicenseConfirmed}
                          />
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                          <button
                            type="button"
                            onClick={() => setEditingSuggestion(null)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Avbryt
                          </button>
                          <button
                            type="button"
                            onClick={handleAddEditedSuggestion}
                            disabled={!suggestionLicenseConfirmed}
                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                              suggestionLicenseConfirmed
                                ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            <Plus className="h-4 w-4" />
                            Legg til
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal suggestion view */
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                            {/* Source status badge */}
                            {suggestion.source_status === 'curated' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium" title="Godkjent kilde fra kurert bibliotek">
                                <Star className="h-3 w-3 fill-green-700" />
                                Godkjent kilde
                              </span>
                            )}
                            {suggestion.source_status === 'ai_verified' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" title="Nytt KI-forslag med verifisert URL">
                                <ShieldCheck className="h-3 w-3" />
                                KI-forslag (verifisert)
                              </span>
                            )}
                            {suggestion.source_status === 'ai_unverified' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full" title="Nytt KI-forslag - må godkjennes">
                                <AlertCircle className="h-3 w-3" />
                                KI-forslag (ikke godkjent)
                              </span>
                            )}
                            {/* URL status badges */}
                            {suggestion.url_modified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full" title="URL erstattet med hovedside">
                                <Link className="h-3 w-3" />
                                Hovedside
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{suggestion.summary}</p>

                          {/* Show original URL if modified */}
                          {suggestion.url_modified && suggestion.original_url && (
                            <p className="text-xs text-amber-600 mt-1">
                              Original URL ikke tilgjengelig: <span className="font-mono">{suggestion.original_url}</span>
                            </p>
                          )}

                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span className="capitalize">{suggestion.literature_type}</span>
                            <span>Relevans: {suggestion.relevance_score}%</span>
                            {suggestion.url && (
                              <a
                                href={suggestion.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-kf-primary hover:underline flex items-center gap-1"
                              >
                                Se kilde
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>

                        <div className="ml-4 flex flex-col gap-2">
                          {/* Edit before adding */}
                          <button
                            type="button"
                            onClick={() => handleEditSuggestion(suggestion)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap flex items-center gap-2"
                            title="Rediger før du legger til"
                          >
                            <Pencil className="h-4 w-4" />
                            Rediger
                          </button>

                          {/* Curated source - just add */}
                          {suggestion.source_status === 'curated' && (
                            <button
                              type="button"
                              onClick={() => handleAcceptSuggestion(suggestion)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm whitespace-nowrap flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Legg til direkte
                            </button>
                          )}

                          {/* AI suggestion - approve and add or just add */}
                          {suggestion.source_status !== 'curated' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApproveAndAddToCurated(suggestion)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm whitespace-nowrap flex items-center gap-2"
                                title="Legg til i studieplanen og send forespørsel om kurering til studieforbundet"
                              >
                                <ThumbsUp className="h-4 w-4" />
                                Legg til & be om kurering
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAcceptSuggestion(suggestion)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm whitespace-nowrap"
                              >
                                Legg til direkte
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowSuggestionsModal(false);
                  setEditingSuggestion(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Literature Modal */}
      {showEditModal && editingLit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4">Rediger litteratur</h3>

            <div className="space-y-4">
              {/* Basic info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forfatter
                  </label>
                  <input
                    type="text"
                    value={editForm.author}
                    onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Ola Nordmann"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgiver
                  </label>
                  <input
                    type="text"
                    value={editForm.publisher}
                    onChange={(e) => setEditForm({ ...editForm, publisher: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Universitetsforlaget"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgivelsesår
                  </label>
                  <input
                    type="number"
                    value={editForm.publication_year}
                    onChange={(e) => setEditForm({ ...editForm, publication_year: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. 2024"
                    min="1900"
                    max="2100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={editForm.isbn}
                    onChange={(e) => setEditForm({ ...editForm, isbn: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="978-82-..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DOI
                  </label>
                  <input
                    type="text"
                    value={editForm.doi}
                    onChange={(e) => setEditForm({ ...editForm, doi: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="10.1000/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  value={editForm.original_url}
                  onChange={(e) => setEditForm({ ...editForm, original_url: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={editForm.literature_type}
                  onChange={(e) => setEditForm({ ...editForm, literature_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                >
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="webpage">Nettside</option>
                  <option value="book">Bok</option>
                  <option value="article">Artikkel</option>
                  <option value="video">Video</option>
                  <option value="manual">Manual/Instruksjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Archive option */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.archive_locally}
                    onChange={(e) => setEditForm({ ...editForm, archive_locally: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Lagre lokalt</span>
                    <p className="text-sm text-gray-500">Last ned og arkiver innholdet for sikker oppbevaring</p>
                  </div>
                </label>
                {editingLit.download_status === 'downloaded' && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Allerede lagret lokalt ({editingLit.local_file_size ? Math.round(editingLit.local_file_size / 1024) + ' KB' : ''})
                  </p>
                )}
              </div>

              {/* License section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-blue-600" />
                    Lisensinformasjon
                  </h4>
                  {editingLit.license_type && !showLicenseInEdit && (
                    <span className="text-sm text-gray-500">
                      Nåværende: {editingLit.license_type === 'public_domain' ? 'Offentlig domene' :
                                 editingLit.license_type === 'creative_commons' ? 'Creative Commons' :
                                 editingLit.license_type === 'open_access' ? 'Åpen tilgang' :
                                 editingLit.license_type}
                    </span>
                  )}
                </div>

                {!showLicenseInEdit ? (
                  <button
                    type="button"
                    onClick={() => setShowLicenseInEdit(true)}
                    className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm flex items-center gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    {editingLit.license_type ? 'Endre lisensinformasjon' : 'Legg til lisensinformasjon'}
                  </button>
                ) : (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <InlineLicenseForm
                      value={editLicenseForm}
                      onChange={setEditLicenseForm}
                      detectedLicense={null}
                      isDetecting={false}
                      confirmed={editLicenseConfirmed}
                      onConfirmChange={setEditLicenseConfirmed}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowLicenseInEdit(false);
                        setEditLicenseConfirmed(false);
                      }}
                      className="mt-3 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Avbryt lisensredigering
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLit(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary"
              >
                Lagre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Correction Modal */}
      {showUrlCorrectionModal && urlCorrectionLit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Search className="h-6 w-6 text-amber-600" />
              Finn alternativ URL
            </h3>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Kilde:</span> {urlCorrectionLit.title}
              </p>
              <p className="text-sm text-gray-500 mt-1 font-mono break-all">
                {urlCorrectionLit.original_url}
              </p>
            </div>

            {loadingUrlSuggestions ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600 mx-auto mb-4" />
                <p className="text-gray-600">Søker etter alternative URLer...</p>
                <p className="text-sm text-gray-500 mt-1">Sjekker Wayback Machine og søker i nettstedets dokumenter</p>
              </div>
            ) : urlSuggestions.length === 0 ? (
              <div className="py-8">
                <div className="text-center mb-6">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Ingen alternative URLer funnet automatisk</p>
                  <p className="text-sm text-gray-500 mt-2">Systemet søkte i Wayback Machine og nettstedets dokumentsider.</p>
                </div>

                {/* Manual URL input as fallback */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-3">
                    Lim inn URL manuelt hvis du har funnet dokumentet:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.target as HTMLInputElement;
                          if (input.value) {
                            handleApplyUrlCorrection(input.value, 'manual');
                          }
                        }
                      }}
                      id="manual-url-input"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('manual-url-input') as HTMLInputElement;
                        if (input?.value) {
                          handleApplyUrlCorrection(input.value, 'manual');
                        }
                      }}
                      disabled={applyingUrl !== null}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {applyingUrl ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Bruk URL
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Velg en alternativ URL for å erstatte den ødelagte lenken:
                </p>
                {urlSuggestions.map((suggestion, index) => (
                  <div key={index} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {suggestion.method === 'wayback' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              <Clock className="h-3 w-3" />
                              Wayback Machine
                            </span>
                          )}
                          {suggestion.method === 'ai_search' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                              <Sparkles className="h-3 w-3" />
                              KI-søk
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            suggestion.status === 'valid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {suggestion.status === 'valid' ? 'Tilgjengelig' : 'Ikke verifisert'}
                          </span>
                          <span className="text-sm text-gray-500">
                            Konfidens: {suggestion.confidence}%
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 mt-2">{suggestion.description}</p>

                        <a
                          href={suggestion.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-kf-primary hover:underline mt-2 inline-flex items-center gap-1 break-all"
                        >
                          {suggestion.url}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>

                        {suggestion.timestamp && (
                          <p className="text-xs text-gray-500 mt-1">
                            Arkivert: {suggestion.timestamp.substring(0, 4)}-
                            {suggestion.timestamp.substring(4, 6)}-
                            {suggestion.timestamp.substring(6, 8)}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleApplyUrlCorrection(suggestion.url, suggestion.method)}
                        disabled={applyingUrl !== null}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
                      >
                        {applyingUrl === suggestion.url ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Oppdaterer...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Bruk denne
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowUrlCorrectionModal(false);
                  setUrlCorrectionLit(null);
                  setUrlSuggestions([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Browser Modal */}
      {showLibraryBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Library className="h-6 w-6 text-indigo-600" />
                Velg fra delt bibliotek
              </h3>
              <button
                type="button"
                onClick={() => setShowLibraryBrowser(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <SharedLiteratureBrowser
                studyPlanId={studyPlanId}
                mode="select"
                onSelect={handleLibrarySelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
